import React from "react";
import { motion } from "framer-motion";

const metrics = [
  {
    category: "Bars & Nightlife",
    stat: "45s",
    label: "Saved per transaction",
    description: "Eliminate hardware drops and POS bottlenecks. Bartenders process payments directly on their personal devices, increasing drink volume by 30% on peak nights.",
    color: "from-indigo-500 to-blue-500",
    align: "left"
  },
  {
    category: "Music Festivals",
    stat: "$0",
    label: "Hardware deployment costs",
    description: "Scale from 10 to 1,000 cashless vendors overnight. No iPads to rent, no card readers to pair. Just download Tapn and start accepting payments in the middle of a field.",
    color: "from-teal-500 to-emerald-500",
    align: "right"
  },
  {
    category: "Luxury Hotels",
    stat: "100%",
    label: "Property coverage",
    description: "From poolside cabanas to rooftop lounges, empower your staff to take payments anywhere on the property without sprinting back to a terminal.",
    color: "from-amber-500 to-orange-500",
    align: "left"
  }
];

export default function ScrollytellingMetrics() {
  return (
    <section className="bg-[#FAFAFA] py-16 sm:py-24 md:py-32 relative border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        <div className="text-center mb-16 sm:mb-24 md:mb-32">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A0A0A] mb-4 sm:mb-6 tracking-tight"
          >
            The Economics of <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-teal-500">Zero Hardware</span>
          </motion.h2>
          <p className="text-gray-500 text-base sm:text-lg md:text-xl max-w-2xl mx-auto font-medium px-2">
            Legacy terminals are expensive anchors. Tapn liberates your operations, driving revenue velocity across every touchpoint.
          </p>
        </div>

        <div className="space-y-16 sm:space-y-24 md:space-y-32 lg:space-y-48">
          {metrics.map((m, i) => (
            <motion.div 
              key={m.category}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`flex flex-col ${m.align === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 sm:gap-12 md:gap-16 lg:gap-24`}
            >
              
              {/* Stat Block */}
              <div className="flex-1 w-full relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${m.color} blur-[80px] opacity-10 rounded-full`} />
                <div className="relative border border-gray-200 bg-white rounded-2xl sm:rounded-[2rem] md:rounded-[3rem] p-8 sm:p-12 md:p-16 lg:p-20 text-center shadow-xl overflow-hidden group">
                  <motion.div 
                    initial={{ scale: 0.5, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 100, delay: 0.2 }}
                    className={`text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-br ${m.color}`}
                  >
                    {m.stat}
                  </motion.div>
                  <div className="text-gray-400 text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-widest uppercase mt-3 sm:mt-4">
                    {m.label}
                  </div>
                </div>
              </div>

              {/* Text Block */}
              <div className="flex-1 w-full text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-gray-200 bg-white mb-4 sm:mb-6 text-xs sm:text-sm font-bold text-gray-500 tracking-widest uppercase shadow-sm">
                  {m.category}
                </div>
                <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0A0A0A] mb-4 sm:mb-6 leading-tight tracking-tight">
                  {m.description.split('.')[0]}.
                </h3>
                <p className="text-gray-500 text-base sm:text-lg md:text-xl leading-relaxed">
                  {m.description.split('.').slice(1).join('.').trim()}
                </p>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
